import { fm } from '@/appLogic/fileManager/baseFileManager';
import { ExcelRow } from '@/coreDomainsModule/translateExcel';
import { utils, WorkSheet } from 'xlsx';
import XLSX from 'xlsx';
import {
  TranslateExcelOrder,
  TranslateExcelOrderBusiness,
  TranslateExcelOrderBusinessResult,
} from '../coreDomainsModule/translateExcelOrder';
import { TranslateExcelOrderBusinessFactory } from './factories/TranslateExcelOrderBusinessFactory';
import { TranslateExcelOrderIdManager } from './translateOrderIdManager';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import {
  DuplicateResolutionRule,
  ImportOrderStatusCondition,
  ImportOrderStatusRule,
  OrderFieldMapping,
  OrderFieldMappings,
  OrderMappingRuleCondition,
  OrderRowMappingRule,
  ParsedOrderCandidate,
  RegisterOrderExcelInfo,
} from '@/coreDomainsModule/entities/InputOrderExcelInfo';
import { DateService } from '@/coreDomainsModule/services/dateService';

/**
 * ファイル情報を読み込み、Excelデータを解析する関数
 * バリデーションはBEに委譲
 * @param file - ファイル情報
 * @param registerOrderExcelInfo - 読み取りマッピング情報
 */
export const handleFileInput = async (
  file: File,
  registerOrderExcelInfo: RegisterOrderExcelInfo
): Promise<TranslateExcelOrderBusinessResult> => {
  const ERROR_RESULT = { splitOrderExcelData: {}, isSuccess: false };
  if (!file) {
    console.error('No file selected');
    return ERROR_RESULT;
  }

  let splitResult: Record<string, TranslateExcelOrderBusiness>;

  try {
    // ファイルをArrayBufferに変換する
    const data = await fm.convertFileToBuffer(file);
    if (data) {
      const workbook = XLSX.read(data, { type: 'array' });
      const excelData: ExcelRow[] = [];

      // 各シートを解析してExcelRow型に変換
      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const headerRowIndex = Math.max(registerOrderExcelInfo.headerLineNumber - 1, 0);
        const parsedRows = parseExcelSheet(file.name, sheetName, worksheet, headerRowIndex);
        excelData.push(...parsedRows);
      });

      // ここでexcelDataを使って必要な処理を行う
      splitResult = splitOrderExcelData(excelData, registerOrderExcelInfo);
      return { splitOrderExcelData: splitResult, isSuccess: true };
    } else {
      console.error('Failed to read file as buffer');
      splitResult = {};
      return ERROR_RESULT;
    }
  } catch (error) {
    console.error('Error reading file:', error);
    splitResult = {};
    return ERROR_RESULT;
  }
};

/**
 * Excelファイルを解析してExcelRow型の配列に変換する関数
 * @param fileName - ファイル名
 * @param sheetName - シート名
 * @param worksheet - Excelシートデータ
 * @param headerRowIndex - 列名として使う行（0が最初の行）
 * @returns ExcelRowの配列
 */
const parseExcelSheet = (fileName: string, sheetName: string, worksheet: WorkSheet, headerRowIndex = 0): ExcelRow[] => {
  const rows: ExcelRow[] = [];
  const sheetData = utils.sheet_to_json(worksheet, { header: 1 }) as (string | number)[][];

  // 指定されたヘッダー行番号を使用して読み出す
  const columnHeaders = sheetData[headerRowIndex] ?? [];

  // ヘッダー行以降をデータ行として処理
  for (let rowIndex = headerRowIndex + 1; rowIndex < sheetData.length; rowIndex++) {
    const row = sheetData[rowIndex];
    const rowData: ExcelRow = {};

    row.forEach((cell, colIndex) => {
      const cellAddress = utils.encode_cell({ c: colIndex, r: rowIndex }); // セルのアドレス (例: "A2", "B2"など)
      const columnName = (columnHeaders[colIndex] as string) || `Column${colIndex + 1}`; // 列名がない場合に備えてfallback

      // セル情報を取得（仮に `worksheet[cellAddress]` でセルデータを取得するとして）
      const cellInfo = worksheet[cellAddress];
      const cellValue = cellInfo?.w || cellInfo?.v; // `w` があればそれを使い、なければ `v` を使用

      rowData[columnName] = {
        value: cellValue,
        address: cellAddress,
        fileName: fileName,
        sheetName: sheetName,
      };
    });

    rows.push(rowData);
  }

  return rows;
};

/**
 * 発注エクセルから取り込んだ情報を整形する
 * @param excelData - エクセルから取り込んだ情報
 * @param registerOrderExcelInfo - 読み取りマッピング情報
 */
const splitOrderExcelData = (
  excelData: ExcelRow[],
  registerOrderExcelInfo: RegisterOrderExcelInfo
): Record<string, TranslateExcelOrderBusiness> => {
  const candidates: ParsedOrderCandidate[] = [];

  for (const row of excelData) {
    const rule = findMatchedRowRule(row, registerOrderExcelInfo.rowMappingRules);
    if (!rule) continue;

    const orderBusiness = mapOrderBusiness(row, rule.fieldMappings);

    const order = mapOrder(row, rule.fieldMappings, rule);

    if (!orderBusiness.translateOrderBusinessId || isEmptyOrder(order)) continue;

    candidates.push({
      orderBusiness,
      order,
      ruleId: rule.id,
      duplicatePriority: rule.duplicatePriority ?? rule.priority,
      duplicateKey: buildDuplicateKey(row, registerOrderExcelInfo.duplicateResolution),
    });
  }

  const resolvedCandidates = resolveDuplicateOrders(candidates, registerOrderExcelInfo.duplicateResolution);

  return buildTranslateExcelOrderInfo(resolvedCandidates);
};

/**
 * 行に適用される取込ルールを取得する
 * @param row - エクセル行データ
 * @param rules - ルールグループ
 */
const findMatchedRowRule = (row: ExcelRow, rules: OrderRowMappingRule[]): OrderRowMappingRule | undefined => {
  return [...rules].sort((a, b) => a.priority - b.priority).find((rule) => matchesAllConditions(row, rule.rowConditions));
};

/**
 * 当該ルールの条件に合致しているかをチェックする
 * @param row - エクセル行データ
 * @param conditions - ルール条件集合
 */
const matchesAllConditions = (row: ExcelRow, conditions: OrderMappingRuleCondition[]): boolean => {
  if (conditions.length === 0) return true;
  return conditions.every((condition) => matchCondition(row, condition));
};

/**
 * 発注取引項目をマッピングする
 * @param row - エクセル行データ
 * @param mappings - マッピング情報
 */
const mapOrderBusiness = (row: ExcelRow, mappings: OrderFieldMappings): TranslateExcelOrderBusiness => {
  const business = TranslateExcelOrderBusinessFactory.create();

  business.warehouseNumber = evaluateMapping(row, mappings.WarehouseNumber);
  business.warehouse = evaluateMapping(row, mappings.Warehouse);
  business.package = evaluateMapping(row, mappings.Package);
  business.businessNumber = evaluateMapping(row, mappings.BusinessNumber);
  business.businessName = evaluateMapping(row, mappings.BusinessName);
  business.supplierNumber = evaluateMapping(row, mappings.SupplierNumber);
  business.supplierName = evaluateMapping(row, mappings.SupplierName);

  business.translateOrderBusinessId = TranslateExcelOrderIdManager.generateOrderIdFormExcelInput(business);

  return business;
};

/**
 * 発注項目をマッピングする
 * @param row - エクセル行データ
 * @param mappings - マッピング情報
 * @param rule - 発注マッピングルール
 */
const mapOrder = (row: ExcelRow, mappings: OrderFieldMappings, rule: OrderRowMappingRule): TranslateExcelOrder => {
  const etaMapping = mappings.EstimatedTimeOfArrival;

  return {
    orderNumber: evaluateMapping(row, mappings.OrderNumber),
    orderIssuedNumber: mappings.OrderIssuedNumber ? evaluateMapping(row, mappings.OrderIssuedNumber) : '',
    amount: Number(evaluateMapping(row, mappings.OrderAmount)),
    crossDate: evaluateMapping(row, mappings.EstimatedTimeOfArrival),
    fixOrderDate: mappings.CreatedOrderDate ? evaluateMapping(row, mappings.CreatedOrderDate) : '',
    status: resolveOrderStatus(row, rule.orderStatusRules, rule.fallbackOrderStatus),
    excelInfo: {
      cellNumber: evaluateMappingAddress(row, etaMapping),
      sheetName: getRowSheetName(row),
      fileName: getRowFileName(row),
    },
  };
};

/**
 * マッピング設定から値を取得する
 * @param row - エクセル行データ
 * @param mapping - マッピング情報
 */
const evaluateMapping = (row: ExcelRow, mapping?: OrderFieldMapping): string => {
  if (!mapping) return '';
  if (mapping.unused) {
    switch (mapping.dataType) {
      case 'string':
        return '-';
      case 'number':
        return '0.01';
      case 'date': {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');

        return `${yyyy}-${mm}-${dd}`;
      }
      default:
        return '';
    }
  }

  switch (mapping.type) {
    case 'column':
      return row[mapping.column]?.value?.toString() ?? '';

    case 'literal':
      return mapping.value;

    case 'concat':
      return mapping.parts.map((part) => evaluateMapping(row, part)).join('');
  }
};

/**
 * マッピング設定から代表セルアドレスを取得する
 * @param row - エクセル行データ
 * @param mapping - マッピング情報
 */
const evaluateMappingAddress = (row: ExcelRow, mapping?: OrderFieldMapping): string => {
  if (!mapping || mapping.unused) return '';

  switch (mapping.type) {
    case 'column':
      return row[mapping.column]?.address?.toString() ?? '';
    case 'literal':
      return '';
    case 'concat':
      return mapping.parts.map((part) => evaluateMappingAddress(row, part)).find((address) => address !== '') ?? '';
  }
};

/**
 * 行データからファイル名を取得する
 * @param row - エクセル行データ
 */
const getRowFileName = (row: ExcelRow): string => {
  const firstCell = row[Object.keys(row)[0]];
  return firstCell?.fileName?.toString() ?? '';
};

/**
 * 行データからシート名を取得する
 * @param row - エクセル行データ
 */
const getRowSheetName = (row: ExcelRow): string => {
  const firstCell = row[Object.keys(row)[0]];
  return firstCell?.sheetName?.toString() ?? '';
};

/**
 * OrderStatusをルール条件を見て解決する
 * @param row - エクセル行データ
 * @param rules - ステータスルール
 * @param fallbackOrderStatus - ルールに一致しない場合のステータス
 */
const resolveOrderStatus = (row: ExcelRow, rules: ImportOrderStatusRule[], fallbackOrderStatus: OrderStatus): OrderStatus => {
  for (const rule of rules) {
    if (rule.conditions.every((condition) => matchCondition(row, condition))) return rule.result;
  }
  return fallbackOrderStatus ?? OrderStatus.ApprovedAwaitingDeliveryDate;
};

/**
 * conditionに合致するかをチェックする
 * @param row - 発注取込エクセルデータ
 * @param condition - condition
 */
const matchCondition = (row: ExcelRow, condition: OrderMappingRuleCondition | ImportOrderStatusCondition): boolean => {
  const headerValue = row[condition.header]?.value;
  switch (condition.operator) {
    case 'is_empty':
      return headerValue == null || headerValue === '';
    case 'is_not_empty':
      return headerValue != null && headerValue !== '';
    case 'equals':
      return condition.valueType === 'number'
        ? Number(headerValue) === Number(condition.value)
        : (headerValue?.toString() ?? '') === (condition.value?.toString() ?? '');
    case 'not_equals':
      return condition.valueType === 'number'
        ? Number(headerValue) !== Number(condition.value)
        : (headerValue?.toString() ?? '') !== (condition.value?.toString() ?? '');
    case 'gt':
      return compareConditionValue(headerValue, condition.value, condition.valueType, (left, right) => left > right);
    case 'gte':
      return compareConditionValue(headerValue, condition.value, condition.valueType, (left, right) => left >= right);
    case 'lt':
      return compareConditionValue(headerValue, condition.value, condition.valueType, (left, right) => left < right);
    case 'lte':
      return compareConditionValue(headerValue, condition.value, condition.valueType, (left, right) => left <= right);
    case 'contains':
      return (headerValue?.toString() ?? '').includes(condition.value?.toString() ?? '');
    case 'not_contains':
      return !(headerValue?.toString() ?? '').includes(condition.value?.toString() ?? '');
  }
};

/**
 * 大小比較用の値に変換して比較する
 * @param headerValue - Excelセルの値
 * @param conditionValue - 条件値
 * @param valueType - 値タイプ
 * @param compare - 比較関数
 */
const compareConditionValue = (
  headerValue: string | number | undefined,
  conditionValue: string | number | null,
  valueType: string,
  compare: (left: number, right: number) => boolean
): boolean => {
  const left = valueType === 'date' ? toDateOnlyComparableValue(headerValue) : Number(headerValue);
  const right = valueType === 'date' ? toDateOnlyComparableValue(conditionValue) : Number(conditionValue);
  if (left == null || right == null || Number.isNaN(left) || Number.isNaN(right)) return false;
  return compare(left, right);
};

/**
 * 日付を時刻を含まない比較用数値に変換する
 * @param value - 変換対象
 */
const toDateOnlyComparableValue = (value: string | number | null | undefined): number | undefined => {
  if (value == null || value === '') return undefined;

  if (typeof value === 'number') {
    const excelSerialDate = DateService.convertExcelSerialToDate(value);
    if (excelSerialDate) return toDateKey(excelSerialDate.year, excelSerialDate.month, excelSerialDate.day);
  }

  const stringValue = String(value).trim();
  const dateMatch = stringValue.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    return toValidatedDateKey(Number(year), Number(month), Number(day));
  }

  const date = new Date(stringValue);
  if (Number.isNaN(date.getTime())) return undefined;
  return toDateKey(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

/**
 * 妥当な年月日を比較用数値に変換する
 * @param year - 年
 * @param month - 月
 * @param day - 日
 */
const toValidatedDateKey = (year: number, month: number, day: number): number | undefined => {
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return undefined;
  return toDateKey(year, month, day);
};

/**
 * 年月日をYYYYMMDDの比較用数値に変換する
 * @param year - 年
 * @param month - 月
 * @param day - 日
 */
const toDateKey = (year: number, month: number, day: number): number => year * 10000 + month * 100 + day;

/**
 * 発注情報が空かどうかを判定する
 * @param order - 取り込んだ発注情報
 */
const isEmptyOrder = (order: TranslateExcelOrder) =>
  !order.orderNumber && !order.orderIssuedNumber && !order.amount && !order.crossDate && !order.fixOrderDate;

/**
 * 重複した発注候補を解決する
 * @param candidates - 発注候補
 * @param duplicateResolution - 重複解決ルール
 */
const resolveDuplicateOrders = (candidates: ParsedOrderCandidate[], duplicateResolution: DuplicateResolutionRule) => {
  if (duplicateResolution.type === 'keepAll') return candidates;

  const grouped = new Map<string, ParsedOrderCandidate[]>();
  const withoutDuplicateKey: ParsedOrderCandidate[] = [];

  for (const candidate of candidates) {
    if (!candidate.duplicateKey) {
      withoutDuplicateKey.push(candidate);
      continue;
    }
    grouped.set(candidate.duplicateKey, [...(grouped.get(candidate.duplicateKey) ?? []), candidate]);
  }

  return [
    ...withoutDuplicateKey,
    ...[...grouped.values()].map((items) => items.sort((a, b) => a.duplicatePriority - b.duplicatePriority)[0]),
  ];
};

/**
 * 重複キーを作成する
 * @param row - エクセル行データ
 * @param duplicateResolution - 重複解決ルール
 */
const buildDuplicateKey = (row: ExcelRow, duplicateResolution: DuplicateResolutionRule): string | undefined => {
  if (duplicateResolution.type === 'keepAll') return undefined;
  const key = duplicateResolution.keyMappings.map((mapping) => evaluateMapping(row, mapping)).join('|');
  return key === '' ? undefined : key;
};

/**
 * 発注候補を発注取込情報に変換する
 * @param resolvedCandidates - 重複解決後の発注候補
 */
const buildTranslateExcelOrderInfo = (resolvedCandidates: ParsedOrderCandidate[]): Record<string, TranslateExcelOrderBusiness> => {
  const result: Record<string, TranslateExcelOrderBusiness> = {};
  for (const candidate of resolvedCandidates) {
    const business = candidate.orderBusiness;
    if (!result[business.translateOrderBusinessId]) {
      result[business.translateOrderBusinessId] = business;
    }

    const orderIndex = Object.keys(result[business.translateOrderBusinessId].excelOrders).length;
    result[business.translateOrderBusinessId].excelOrders[orderIndex] = candidate.order;
  }
  return result;
};
